// services/learningApi.js

import axios from "axios";
import { makeRequest } from "../utils/handleJWT";
import { checkErrorMessage } from "../utils/responseHandler";
import { decryptLearningPayload } from "../utils/learningPayloadCrypto";
import { getSingleCollection } from "./subtitleApi";

const LEARNING_API_URL = "https://learning.umlang.com/learning/api/v1/learning";
const LEARNING_VOTE_API_URL =
  "https://learningvote.umlang.com/learningvote/api/v1/vote";

/**
 * دریافت و decrypt داده‌های یادگیری یک زیرنویس
 *
 * پاسخ decrypt شده به‌صورت JSON پارس‌شده برمی‌گردد و مصرف‌کننده
 * هیچ اطلاعی از جزئیات encryption ندارد.
 *
 * @param {string} subtitleUploadId
 * @returns {Promise<Object>} داده decrypt شده (مثل { status, subtitleUploadId, items })
 */
export const fetchUserLearningData = async (subtitleUploadId) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    try {
      const response = await axios.get(
        `${LEARNING_API_URL}/user/${subtitleUploadId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response.data;

      if (!responseData || responseData.success !== true) {
        throw new Error(
          responseData?.message || "خطا در دریافت داده‌های یادگیری"
        );
      }

      if (!responseData.key || !responseData.payload) {
        throw new Error("کلید یا payload در پاسخ یافت نشد");
      }

      return await decryptLearningPayload({
        key: responseData.key,
        payload: responseData.payload,
      });
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        const err = new Error(
          errorDetails.message || "خطا در دریافت داده‌های یادگیری"
        );
        err.errorCode = error.response?.data?.errorCode || null;
        throw err;
      }
      throw error;
    }
  });
};

/**
 * جستجوی زیرنویس‌هایی که تحلیل لغات و اصطلاحات آن‌ها کامل شده است
 *
 * @param {Object} params
 * @param {string} params.nameOfMovie نام فیلم یا سریال
 * @param {string} params.movieType "movie" یا "series"
 * @param {string|number} [params.season] شماره فصل (فقط برای سریال)
 * @param {string|number} [params.episode] شماره قسمت (فقط برای سریال)
 * @returns {Promise<Object>} نتیجه صفحه‌بندی‌شده (docs, totalDocs, ...)
 */
export const searchCompletedSubtitles = async ({
  nameOfMovie,
  movieType,
  season,
  episode,
}) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    const body = {
      nameOfMovie,
      movieType: movieType || "movie",
    };

    if (movieType === "series") {
      if (season != null && season !== "") {
        body.season = parseInt(season, 10);
      }
      if (episode != null && episode !== "") {
        body.episode = parseInt(episode, 10);
      }
    }

    try {
      const response = await axios.post(
        `${LEARNING_API_URL}/user/findSubtitles`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response.data;

      if (!responseData || responseData.success !== true) {
        throw new Error(responseData?.message || "خطا در جستجو");
      }

      return responseData.data;
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        throw new Error(errorDetails.message || "خطا در جستجو");
      }
      throw error;
    }
  });
};

/**
 * ثبت درخواست استخراج لغات و اصطلاحات (رأی) برای یک کالکشن زیرنویس
 *
 * @param {Object} params
 * @param {string} params.collectionId شناسه کالکشن (اجباری در سمت سرور)
 * @param {string} [params.subtitleUploadId] آی‌دی واقعی زیرنویس اول (اختیاری؛
 *   اگر ارسال شود باید با collection.subOneID یکسان باشد)
 * @returns {Promise<Object>} پاسخ سرویس رأی (شامل alreadyVoted/alreadyExists)
 */
export const submitVoteRequest = async ({ collectionId, subtitleUploadId }) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    try {
      const response = await axios.post(
        `${LEARNING_VOTE_API_URL}/vote`,
        { collectionId, subtitleUploadId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response.data;

      if (!responseData || responseData.success !== true) {
        throw new Error(
          responseData?.message || "خطا در ثبت درخواست استخراج لغات"
        );
      }

      return responseData;
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        const err = new Error(
          errorDetails.message || "خطا در ثبت درخواست استخراج لغات"
        );
        err.errorCode = error.response?.data?.errorCode || null;
        throw err;
      }
      throw error;
    }
  });
};

/**
 * دریافت اطلاعات یک کالکشن به‌همراه آمار رأی و وضعیت کاربر
 *
 * ابتدا سعی می‌کند از endpoint غنی سرویس رأی (vote/collection/:id) پاسخ بگیرد؛
 * اگر آن endpoint در دسترس نباشد (مثلاً هنوز deploy نشده)، به‌صورت خودکار از
 * ترکیب دو endpoint موجود استفاده می‌کند:
 *   • getCollectionSubtitles (سرویس subtitles، عمومی) برای اطلاعات کالکشن
 *   • vote/subtitle/:id (سرویس رأی) برای آمار رأی زیرنویس شماره ۱
 *
 * بدون توکن هم کار می‌کند (فقط اطلاعات عمومی). در صورت ارسال توکن معتبر و
 * includeUserStatus، وضعیت رأی کاربر به‌صورت best-effort نیز محاسبه می‌شود.
 *
 * @param {Object} params
 * @param {string} params.collectionId شناسه کالکشن
 * @param {boolean} [params.includeUserStatus] محاسبه وضعیت رأی کاربر (fقط fallback)
 * @returns {Promise<Object>} پاسخ سرویس شامل اطلاعات کالکشن و آمار رأی
 */
export const getCollectionVoteInfo = async ({
  collectionId,
  includeUserStatus = false,
} = {}) => {
  if (!collectionId) {
    throw new Error("شناسه کالکشن وارد نشده است");
  }

  const token = localStorage.getItem("token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchVotesForSubtitle = async (subtitleUploadId) => {
    try {
      const response = await axios.get(
        `${LEARNING_VOTE_API_URL}/subtitle/${encodeURIComponent(
          subtitleUploadId
        )}`,
        { headers: authHeaders }
      );
      return {
        totalVotes: response?.data?.data?.totalVotes ?? 0,
        activeVotes: response?.data?.data?.activeVotes ?? 0,
      };
    } catch (ignore) {
      return { totalVotes: 0, activeVotes: 0 };
    }
  };

  const hasUserVotedFor = async (id) => {
    if (!token) return false;
    for (let page = 1; page <= 5; page++) {
      let response;
      try {
        response = await axios.post(
          `${LEARNING_VOTE_API_URL}/my-votes`,
          { page, limit: 50 },
          { headers: authHeaders }
        );
      } catch (ignore) {
        return false;
      }
      const data = response?.data?.data;
      const items = data?.items || [];
      if (items.some((vote) => vote.collectionId === id)) return true;
      const pagination = data?.pagination;
      if (!pagination || !pagination.hasNextPage) break;
    }
    return false;
  };

  const buildFromDeployedApis = async () => {
    const collectionResponse = await getSingleCollection(collectionId);
    const payload = collectionResponse?.data;
    const collection =
      payload?.data?.[0] ||
      (payload && payload.collectionId ? payload : null);
    if (!collection) {
      const error = new Error("مجموعه زیرنویس یافت نشد");
      error.errorCode = "COLLECTION_NOT_FOUND";
      throw error;
    }

    const subOneID = collection.subOneID || null;
    const aggregate = subOneID
      ? await fetchVotesForSubtitle(subOneID)
      : { totalVotes: 0, activeVotes: 0 };

    let userHasVoted = false;
    if (includeUserStatus) {
      userHasVoted = await hasUserVotedFor(collectionId);
    }

    return {
      success: true,
      data: {
        collectionId: collection.collectionId,
        movieName: collection.movieName,
        movieFileName: collection.movieFileName,
        coverUrl: collection.coverUrl,
        season: collection.season ?? null,
        episode: collection.episode ?? null,
        videoType: collection.videoType || "movie",
        langOne: collection.langOne,
        langTwo: collection.langTwo,
        subOneID,
        subTwoID: collection.subTwoID,
        collectionType: collection.collectionType || "normalUser",
        status: collection.status || null,
        rejected: collection.status === "rejected",
        aggregate,
        learningExists: false,
        learningStatus: null,
        userHasVoted,
      },
    };
  };

  try {
    const response = await axios.get(
      `${LEARNING_VOTE_API_URL}/collection/${encodeURIComponent(collectionId)}`,
      { headers: authHeaders }
    );
    return response.data;
  } catch (primaryError) {
    // endpoint غنی در دسترس نیست → مسیر fallback
    try {
      return await buildFromDeployedApis();
    } catch (fallbackError) {
      const error = new Error(
        fallbackError?.message || "خطا در دریافت اطلاعات کالکشن"
      );
      error.errorCode =
        fallbackError?.errorCode ||
        primaryError?.response?.data?.errorCode ||
        null;
      throw error;
    }
  }
};

/**
 * دریافت درخواست‌های ثبت‌شده‌ی کاربر (رأی‌های کاربر)
 *
 * @param {Object} params
 * @param {number} [params.page] شماره صفحه (پیش‌فرض ۱)
 * @param {number} [params.limit] تعداد در هر صفحه (پیش‌فرض ۲۰، حداکثر ۱۰۰)
 * @returns {Promise<Object>} پاسخ سرویس شامل items و pagination
 */
export const getMyVotes = async ({ page = 1, limit = 20 } = {}) => {
  return makeRequest(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("لطفاً ابتدا وارد شوید");
    }

    try {
      const response = await axios.post(
        `${LEARNING_VOTE_API_URL}/my-votes`,
        { page, limit },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const responseData = response.data;

      if (!responseData || responseData.success !== true) {
        throw new Error(responseData?.message || "خطا در دریافت درخواست‌های شما");
      }

      return responseData;
    } catch (error) {
      if (error.response) {
        const errorDetails = checkErrorMessage(error);
        const err = new Error(errorDetails.message || "خطا در دریافت درخواست‌های شما");
        err.errorCode = error.response?.data?.errorCode || null;
        throw err;
      }
      throw error;
    }
  });
};